import { useState, useRef, useCallback, useEffect } from 'react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

/**
 * Hook for browser-native speech-to-text via the Web Speech API.
 *
 * @param {object}   opts
 * @param {string}   opts.lang            – BCP-47 language tag (default 'en-US')
 * @param {function} opts.getInputValue   – returns the current text in the input
 * @param {function} opts.setInputValue   – sets the input text
 * @returns {{ isListening, isSupported, startListening, stopListening, cancelListening }}
 */
export default function useSpeechToText({
  lang = 'en-US',
  getInputValue,
  setInputValue,
} = {}) {
  const isSupported = !!SpeechRecognition;

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');       // text that existed before mic was pressed
  const finalBufferRef = useRef('');    // finalized speech accumulated this session

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* noop */ }
      }
    };
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) return;

    // Capture whatever is currently in the input as the base
    const currentText = getInputValue ? getInputValue() : '';
    baseTextRef.current = currentText;
    finalBufferRef.current = '';

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let newFinals = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinals += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (newFinals) {
        finalBufferRef.current += newFinals;
      }

      // Build the full displayed value: base + finalized speech + live interim
      const base = baseTextRef.current;
      const spacer = base && !base.endsWith(' ') && (finalBufferRef.current || interimTranscript) ? ' ' : '';
      const composed = base + spacer + finalBufferRef.current + interimTranscript;

      if (setInputValue) setInputValue(composed);
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        // Permission denied — stop gracefully
        setIsListening(false);
      }
      // 'aborted' errors are expected when we call .stop() / .abort()
    };

    recognition.onend = () => {
      setIsListening(false);
      // Commit whatever we have
      const base = baseTextRef.current;
      const spacer = base && !base.endsWith(' ') && finalBufferRef.current ? ' ' : '';
      if (setInputValue && finalBufferRef.current) {
        setInputValue(base + spacer + finalBufferRef.current);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isSupported, lang, getInputValue, setInputValue]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
    }
    // isListening will be set to false by the onend handler
  }, []);

  const cancelListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* noop */ }
    }
    setIsListening(false);
  }, []);

  return { isListening, isSupported, startListening, stopListening, cancelListening };
}
